// The Google dashboard's chart surface — the Meta chart system, fed Google data.
//
// Three pieces, mirroring the Meta reporting page:
//   · KPI tiles carry a MINI-CHART of their own metric (ReportingMiniChart).
//   · A CHART MENU turns the page's charts on and off (the ReportingChartsMenu
//     pattern) — Trend and Auction Competitiveness are both first-class and both
//     optional; Competitiveness is the genuinely Google-specific one and stays.
//   · A BIG CHART underneath, driven by the current selection: click a KPI tile to
//     plot that metric, and it follows the drill focus because the whole page
//     already re-queries on focus.
//
// Everything renders through the Meta components (shadcn chart primitives), so the
// two dashboards share one visual language and one set of bucket/gap rules.

import { useRef, useState } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { useClickOutside } from '../../lib/useClickOutside';
import { cn } from '../../lib/utils';
import { ReportingMiniChart } from '../reporting/ReportingMiniChart';
import { ChartStyleToggle } from '../reporting/ChartStyleToggle';
import { ChartGripHandle } from '../reporting/ReportingChartGrid';
import { PanelTitle } from '../reporting/PanelTitle';
import { formatBucketLabel, TREND_GROUPINGS, type TrendGrouping } from '../reporting/chartDate';
import { formatMetricValue, type TrendChartData } from '../reporting/trendSeries';
import type { ChartStyle } from '../reporting/chartPrefs';
import { tileStyleFor } from './googleAdsViewPrefs';
import { metricDef } from '../../lib/googleAdsMetrics';
import { NOT_MEASURED, formatMetric, isMetricUnmeasured } from './metricFormat';
import type { GoogleAdsMetricKey, GoogleAdsMetrics } from '../../types/googleAdsReporting';
import {
  DEFAULT_GOOGLE_CHARTS,
  GOOGLE_CHARTS,
  sanitizeCharts,
  type GoogleChartKey,
} from './googleAdsChartCatalog';

// The chart catalogue (GoogleChartKey, GOOGLE_CHARTS, DEFAULT_GOOGLE_CHARTS,
// sanitizeCharts) now lives in the LEAF module googleAdsChartCatalog (imported
// above) to break a circular import with googleAdsViewPrefs — this component
// imports tileStyleFor from there, and it imported these constants back, which
// left DEFAULT_GOOGLE_CHARTS undefined at module-init in the prod bundle. See that
// file's header. Re-exported here so existing importers of './GoogleAdsCharts'
// resolve unchanged.
export { DEFAULT_GOOGLE_CHARTS, GOOGLE_CHARTS, sanitizeCharts, type GoogleChartKey };

export function GoogleChartsMenu({
  selected,
  grouping,
  onChange,
  onGroupingChange,
}: {
  selected: readonly GoogleChartKey[];
  grouping: TrendGrouping;
  onChange: (next: GoogleChartKey[]) => void;
  onGroupingChange: (next: TrendGrouping) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside([ref], () => setOpen(false), open);

  const toggle = (key: GoogleChartKey): void => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(GOOGLE_CHARTS.filter((c) => next.has(c.key)).map((c) => c.key));
  };

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title="Choose which charts to show"
        className="inline-flex h-8 items-center gap-1.5 rounded-md border border-input bg-background px-2 text-sm hover:bg-accent"
      >
        <SlidersHorizontal className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        <span className="text-xs text-muted-foreground">Charts</span>
        <span className="font-medium">{selected.length}</span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1 w-64 rounded-md border border-border bg-popover p-2 text-sm shadow-lg"
        >
          {GOOGLE_CHARTS.map((c) => (
            <label
              key={c.key}
              className="flex cursor-pointer items-start gap-2 rounded px-1.5 py-1.5 hover:bg-accent"
            >
              <input
                type="checkbox"
                checked={selected.includes(c.key)}
                onChange={() => toggle(c.key)}
                className="mt-0.5"
              />
              <span>
                <span className="block">{c.label}</span>
                <span className="block text-xs text-muted-foreground">{c.hint}</span>
              </span>
            </label>
          ))}
          <div className="mt-2 border-t border-border pt-2">
            <div className="mb-1 px-1.5 text-xs text-muted-foreground">Group buckets by</div>
            <div className="flex gap-1 px-1.5">
              {TREND_GROUPINGS.map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => onGroupingChange(g)}
                  className={cn(
                    'rounded-full border px-2 py-0.5 text-xs capitalize',
                    g === grouping
                      ? 'border-kyck-600 bg-kyck-50 text-kyck-800'
                      : 'border-input text-muted-foreground hover:bg-accent',
                  )}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// KPI tiles with mini-charts
// ---------------------------------------------------------------------------

/**
 * ONE KPI tile. Rendered as a panel inside the shared ReportingChartGrid (the
 * tiles region), so the old wrapping `grid grid-cols-2 md:grid-cols-4` div is
 * gone — the grid's default layout reproduces that 4-up row.
 */
export function GoogleAdsKpiTile({
  metricKey,
  metrics,
  series,
  style,
  onStyleChange,
  isActive,
  onSelect,
  loading,
}: {
  metricKey: GoogleAdsMetricKey;
  metrics: GoogleAdsMetrics | undefined;
  /** This metric's mini-series, already bucketed. */
  series: TrendChartData | undefined;
  /** PER-TILE chart style (the Meta hero-card model). */
  style: ChartStyle;
  onStyleChange: (key: GoogleAdsMetricKey, style: ChartStyle) => void;
  isActive: boolean;
  onSelect: (key: GoogleAdsMetricKey) => void;
  loading: boolean;
}): React.JSX.Element {
  const def = metricDef(metricKey);
  // An unmeasured metric is shown MUTED with an em dash and a reason, never
  // as 0 — see metricFormat for why that distinction matters commercially.
  const unmeasured = metrics ? isMetricUnmeasured(metrics, metricKey) : false;
  return (
    // ⚠️ A DIV with role="button", not a <button> — the Meta HeroStatCard
    // does the same, because the style toggle, the drag grip and the
    // interactive mini-chart are themselves interactive and cannot legally nest
    // inside a button. The toggle and the grip stop propagation so neither
    // changing a style nor dragging the tile also selects it.
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(metricKey)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(metricKey);
        }
      }}
      aria-pressed={isActive}
      title={def?.hint ?? `Plot ${def?.label ?? metricKey} below`}
      className={cn(
        'cursor-pointer rounded-lg border bg-card p-4 text-left transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isActive ? 'border-kyck-600 ring-1 ring-kyck-600' : 'border-border hover:bg-accent/40',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        {/* The shared panel-title treatment (see PanelTitle) — a tile's label
            reads at the same weight/contrast as the section headings beside it
            on the same canvas. */}
        <PanelTitle selected={isActive} title={def?.label ?? metricKey}>
          {def?.label ?? metricKey}
        </PanelTitle>
        <div className="flex shrink-0 items-center gap-0.5">
          <ChartGripHandle
            title={`Drag to move ${def?.label ?? metricKey} · resize from its bottom-right corner`}
          />
          <ChartStyleToggle value={style} onChange={(next) => onStyleChange(metricKey, next)} />
        </div>
      </div>
      <div
        className={cn(
          'mt-1 text-2xl font-semibold tabular-nums',
          unmeasured && 'text-muted-foreground',
        )}
      >
        {loading && !metrics ? (
          <span className="inline-block h-7 w-24 animate-pulse rounded bg-muted" />
        ) : metrics ? (
          formatMetric(metrics, metricKey)
        ) : (
          NOT_MEASURED
        )}
      </div>
      {unmeasured && !loading ? (
        <div className="mt-1 text-xs text-muted-foreground">Not reported for this scope</div>
      ) : (
        // The mini-chart rides the tile — the same component the Meta hero
        // cards use, so the visual language is identical. The grow class is what
        // lets a RESIZED tile fill with chart rather than blank space; see the
        // note in index.css.
        series && (
          <div className="reporting-chart-panel-grow mt-3">
            <ReportingMiniChart data={series} style={style} />
          </div>
        )
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The big, selection-driven chart
// ---------------------------------------------------------------------------

/**
 * The large chart. It plots whichever KPI tile is selected, over the current
 * scope — so clicking a tile or drilling a Location/Campaign both update it (the
 * drill re-queries the summary, which is this chart's source).
 *
 * Axes/series are DIRECT children of the chart element and the type is switched
 * with inline conditional JSX: recharts discovers graphical children via
 * React.Children.forEach, which does NOT descend into fragments — a
 * fragment-wrapped Bar/Line/Area renders blank. Same constraint the Meta charts
 * document.
 */
export function GoogleAdsBigChart({
  data,
  style,
  onStyleChange,
  scopeLabel,
  loading,
}: {
  data: TrendChartData | undefined;
  style: ChartStyle;
  onStyleChange: (s: ChartStyle) => void;
  /** What the chart is currently scoped to (the drill focus), for the header. */
  scopeLabel: string;
  loading: boolean;
}): React.JSX.Element {
  const config: ChartConfig = {
    value: { label: data?.metric.label ?? 'value', color: 'hsl(var(--chart-1))' },
  };

  const xAxis = (
    <XAxis
      dataKey="bucket"
      tick={{ fontSize: 11 }}
      tickMargin={8}
      tickFormatter={(v) => formatBucketLabel(String(v), data?.grouping ?? 'daily')}
    />
  );
  const yAxis = <YAxis tick={{ fontSize: 11 }} width={64} />;
  const grid = <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />;
  const tooltip = (
    <ChartTooltip
      cursor={{ fill: 'hsl(var(--muted))', opacity: 0.35 }}
      content={
        <ChartTooltipContent
          labelFormatter={(v) => formatBucketLabel(String(v), data?.grouping ?? 'daily')}
          formatter={(value) => (
            <span className="font-mono font-medium tabular-nums text-foreground">
              {formatMetricValue(data?.format ?? 'count', Number(value), data?.currency ?? null)}
            </span>
          )}
        />
      }
    />
  );
  const common = { data: data?.rows ?? [], margin: { top: 8, right: 8, bottom: 4, left: 8 } };

  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold capitalize">{data?.metric.label ?? 'Trend'}</h2>
          <p className="text-xs text-muted-foreground">
            {scopeLabel}
            {data?.currency && <> · {data.currency}</>}
          </p>
        </div>
        <ChartStyleToggle value={style} onChange={onStyleChange} />
      </header>

      {loading && !data ? (
        <div className="h-64 w-full animate-pulse rounded bg-muted" />
      ) : !data || data.isEmpty ? (
        <p className="py-16 text-center text-sm text-muted-foreground">No data in this range.</p>
      ) : (
        <ChartContainer config={config} className="aspect-auto h-64 w-full">
          {style === 'line' ? (
            <LineChart {...common}>
              {grid}
              {xAxis}
              {yAxis}
              {tooltip}
              <Line
                type="monotone"
                dataKey="value"
                name={data.metric.label}
                stroke="var(--color-value)"
                dot={false}
                strokeWidth={2}
                connectNulls
                isAnimationActive={false}
              />
            </LineChart>
          ) : style === 'area' ? (
            <AreaChart {...common}>
              {grid}
              {xAxis}
              {yAxis}
              {tooltip}
              <Area
                type="monotone"
                dataKey="value"
                name={data.metric.label}
                stroke="var(--color-value)"
                fill="var(--color-value)"
                fillOpacity={0.18}
                strokeWidth={2}
                connectNulls
                isAnimationActive={false}
              />
            </AreaChart>
          ) : (
            <BarChart {...common}>
              {grid}
              {xAxis}
              {yAxis}
              {tooltip}
              <Bar
                dataKey="value"
                name={data.metric.label}
                fill="var(--color-value)"
                radius={2}
                isAnimationActive={false}
              />
            </BarChart>
          )}
        </ChartContainer>
      )}
    </section>
  );
}
